import Service, { type Registry as Services, service } from '@ember/service';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

export default class TotalRevenueDateService extends Service {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service('total-revenue/myhotels')
  declare totalRevenueMyhotels: Services['total-revenue/myhotels'];

  /**
   * Default selected date range is current month to last available day, e.g.:
   * - last available day June 15 => selected range June 1 - June 15 => fromDate June 1
   * - last available day June 30 => selected range June 1 - June 30 => fromDate June 1
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get defaultFromDateInLocalTime() {
    return this.maxDateInLocalTime.startOf('month');
  }

  /**
   * Default selected date range is current month to last available day, e.g.:
   * - last available day June 15 => selected range June 1 - June 15 => toDate June 15
   * - last available day June 30 => selected range June 1 - June 30 => toDate June 30
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get defaultToDateInLocalTime() {
    return this.maxDateInLocalTime.startOf('day');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get last90DaysRange() {
    const end = this.maxDateInLocalTime;

    return {
      start: end.subtract(90, 'day'),
      end,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get maxDateInLocalTime() {
    const lastCompleteStayDates = this.totalRevenueMyhotels.properties.map(
      (property) => property.lastCompleteStayDate,
    );

    const max = dayjs.max(lastCompleteStayDates) ?? dayjs();

    // A "last complete stay date" can never be in the future. However, guard
    // against bad upstream data which would mess up the datepicker presets.
    return dayjs.min(max, dayjs());
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get minDateInLocalTime() {
    const firstCompleteStayDates = this.totalRevenueMyhotels.properties.map(
      (property) => property.firstCompleteStayDate,
    );

    return dayjs.min(firstCompleteStayDates) as Dayjs;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get monthToDateRange() {
    const end = this.maxDateInLocalTime;

    return {
      start: end.startOf('month'),
      end,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get quarterToDateRange() {
    const end = this.maxDateInLocalTime;

    return {
      start: end.startOf('quarter'),
      end,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get yearToDateRange() {
    const end = this.maxDateInLocalTime;

    return {
      start: end.startOf('year'),
      end,
    };
  }

  /**
   * Returns fromDate in local time if passed, otherwise fallback to default fromDate
   * @param {*} fromDate - date string in YYYY-MM-DD format
   * @returns {dayjs} dayjs object in local time
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getFromDateInLocalTimeWithFallback(fromDate?: string) {
    return fromDate
      ? this.hotelAndMarketService.toLocalTime(fromDate)
      : this.defaultFromDateInLocalTime;
  }

  /**
   * Returns toDate in local time if passed, otherwise fallback to default toDate
   * @param {*} toDate - date string in YYYY-MM-DD format
   * @returns {dayjs} dayjs object in local time
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getToDateInLocalTimeWithFallback(toDate?: string) {
    return toDate
      ? this.hotelAndMarketService.toLocalTime(toDate)
      : this.defaultToDateInLocalTime;
  }
}

declare module '@ember/service' {
  interface Registry {
    'total-revenue/date': TotalRevenueDateService;
  }
}
